/**
 * Firebase Auth service: real sign-in when config has firebaseConfig.
 * Composes auth-firebase-load (scripts), auth-firebase-providers (popup/link), auth-firebase-overlay (UI).
 */

import type { AuthService, LecturaUser } from '../core/types';
import { ensureFirestoreDb } from './firestore-service';
import {
  loadFirebaseAuthScripts,
  toLecturaUser,
  type FirebaseAuthInstance,
  type FirebaseUser
} from './auth-firebase-load';
import {
  signInWithPopupAndLink,
  getReauthProvider,
  type AuthNamespace
} from './auth-firebase-providers';
import { createLoginOverlay } from './auth-firebase-overlay';
import { buildUserDirectoryNameSearchFields } from './user-directory-name-tokens';

export function createFirebaseAuthService(firebaseConfig: unknown): AuthService {
  if (!firebaseConfig || typeof firebaseConfig !== 'object') {
    return {
      getCurrentUser: () => null,
      isAuthReady: () => true
    };
  }

  let currentUser: LecturaUser | null = null;
  let authReady = false;
  let unsubAuth: (() => void) | null = null;
  const listeners = new Set<() => void>();
  const pendingCred = { value: null as unknown, get: () => pendingCred.value, set: (c: unknown) => { pendingCred.value = c; }, clear: () => { pendingCred.value = null; } };

  function notify(): void {
    listeners.forEach((cb) => cb());
  }

  function getFirebaseAuth(): FirebaseAuthInstance {
    const fb = (typeof window !== 'undefined' && window.firebase) || null;
    if (!fb || typeof fb.auth !== 'function') throw new Error('Firebase not loaded');
    return (fb.auth as () => FirebaseAuthInstance)();
  }

  function getAuthNamespace(): AuthNamespace {
    const fb = typeof window !== 'undefined' ? window.firebase : undefined;
    if (!fb || !fb.auth) throw new Error('Firebase not loaded');
    return fb.auth as unknown as AuthNamespace;
  }

  function syncUserDirectoryEntry(user: FirebaseUser | null): Promise<void> {
    if (!user || user.isAnonymous || !user.uid || !user.email) return Promise.resolve();
    return ensureFirestoreDb({ firebaseConfig })
      .then((db) => {
        const ref = db.collection('userDirectory').doc(String(user.uid)) as unknown as {
          set(data: Record<string, unknown>, opts?: { merge?: boolean }): Promise<void>;
        };
        const email = String(user.email || '').trim().toLowerCase();
        const displayName = String(user.displayName || '').trim();
        const photoURL = String(user.photoURL || '').trim();
        const nameTok = buildUserDirectoryNameSearchFields(displayName);
        return ref.set(
          {
            uid: String(user.uid || '').trim(),
            email,
            emailLower: email,
            displayName: displayName || null,
            displayNameLower: displayName ? displayName.toLowerCase() : null,
            dirNameToken0: nameTok.dirNameToken0,
            dirNameToken1: nameTok.dirNameToken1,
            dirNameTokenLast: nameTok.dirNameTokenLast,
            photoURL: photoURL || null,
            updatedAt: Date.now()
          },
          { merge: true }
        );
      })
      .catch(() => {});
  }

  function initAuth(): void {
    const fb = typeof window !== 'undefined' ? window.firebase : undefined;
    if (!fb || !fb.auth) return;
    let app: { name: string };
    try {
      app = fb.app ? fb.app() : (null as unknown as { name: string });
    } catch {
      if (fb.initializeApp) app = fb.initializeApp(firebaseConfig) as { name: string };
      else return;
    }
    if (unsubAuth) return;
    unsubAuth = fb.auth!().onAuthStateChanged((user: FirebaseUser | null) => {
      authReady = true;
      currentUser = toLecturaUser(user) as LecturaUser;
      if (typeof console !== 'undefined' && typeof console.debug === 'function') {
        console.debug('[lectura-next][auth]', {
          event: 'onAuthStateChanged',
          uid: currentUser?.uid || null,
          email: currentUser?.email || null,
          isAnonymous: !!currentUser?.isAnonymous,
          authReady,
          hasFirebaseUser: !!user,
          firebaseIsAnonymous: !!user?.isAnonymous
        });
      }
      if (!user && fb.auth) {
        fb.auth!().signInAnonymously().catch(() => {});
      }
      syncUserDirectoryEntry(user).catch(() => {});
      notify();
    });
    notify();
  }

  function ensureAuth(cb: (err?: Error) => void): void {
    if (authReady && unsubAuth) {
      cb();
      return;
    }
    if (typeof window !== 'undefined' && window.firebase && typeof (window.firebase as { auth?: () => unknown }).auth === 'function') {
      try {
        initAuth();
        cb();
      } catch (e) {
        authReady = true;
        notify();
        cb(e instanceof Error ? e : new Error('Firebase init failed'));
      }
      return;
    }
    loadFirebaseAuthScripts()
      .then(() => {
        initAuth();
        cb();
      })
      .catch((err) => {
        authReady = true;
        notify();
        cb(err instanceof Error ? err : new Error(String(err)));
      });
  }

  function doSignInWithGoogle(): void {
    overlay.setError('Loading sign-in…');
    ensureAuth((loadErr) => {
      if (loadErr) {
        overlay.setError(loadErr.message === 'No Firebase config' ? 'Sign-in is not configured for this presentation.' : loadErr.message + ' Check your connection and try again.');
        return;
      }
      try {
        const GoogleAuthProvider = getAuthNamespace().GoogleAuthProvider;
        if (!GoogleAuthProvider) {
          overlay.setError('Google sign-in not available.');
          return;
        }
        const provider = new GoogleAuthProvider();
        overlay.setError('');
        signInWithPopupAndLink(getFirebaseAuth, provider, pendingCred, () => overlay.hide(), (err) => overlay.setError(err?.message || 'Sign-in failed. Try again.'));
      } catch {
        overlay.setError('Could not start sign-in. Try again.');
      }
    });
  }

  function doSignInWithGithub(): void {
    overlay.setError('Loading sign-in…');
    ensureAuth((loadErr) => {
      if (loadErr) {
        overlay.setError(loadErr.message === 'No Firebase config' ? 'Sign-in is not configured.' : loadErr.message + ' Check your connection and try again.');
        return;
      }
      try {
        const GithubAuthProvider = getAuthNamespace().GithubAuthProvider;
        if (!GithubAuthProvider) {
          overlay.setError('GitHub sign-in not available.');
          return;
        }
        const provider = new GithubAuthProvider();
        overlay.setError('');
        signInWithPopupAndLink(getFirebaseAuth, provider, pendingCred, () => overlay.hide(), (err) => overlay.setError(err?.message || 'Sign-in failed. Try again.'));
      } catch {
        overlay.setError('Could not start sign-in. Try again.');
      }
    });
  }

  const overlay = createLoginOverlay({ onGoogleClick: doSignInWithGoogle, onGithubClick: doSignInWithGithub });

  function signOut(): void {
    try {
      getFirebaseAuth().signOut();
      overlay.hide();
    } catch {
      // ignore
    }
  }

  function reauthenticateCurrentUser(): Promise<void> {
    return new Promise((resolve, reject) => {
      ensureAuth((loadErr) => {
        if (loadErr) {
          reject(loadErr);
          return;
        }
        const auth = getFirebaseAuth();
        const user = auth.currentUser as FirebaseUser | null;
        if (!user || user.isAnonymous) {
          reject(new Error('Sign in again before deleting your account.'));
          return;
        }
        const provider = getReauthProvider(getAuthNamespace(), user);
        if (!provider || typeof user.reauthenticateWithPopup !== 'function') {
          reject(new Error('Re-authentication is not available for this account.'));
          return;
        }
        user.reauthenticateWithPopup(provider).then(() => resolve()).catch(reject);
      });
    });
  }

  function showLoginOverlay(): void {
    ensureAuth(() => {});
    overlay.show();
  }

  function getIdToken(): Promise<string | null> {
    return new Promise((resolve) => {
      ensureAuth((loadErr) => {
        if (loadErr) {
          resolve(null);
          return;
        }
        try {
          const u = getFirebaseAuth().currentUser as FirebaseUser | null;
          if (!u || typeof u.getIdToken !== 'function') {
            resolve(null);
            return;
          }
          u.getIdToken()
            .then((t) => resolve(t ? String(t) : null))
            .catch(() => resolve(null));
        } catch {
          resolve(null);
        }
      });
    });
  }

  function deleteCurrentUser(): Promise<void> {
    return new Promise((resolve, reject) => {
      ensureAuth((loadErr) => {
        if (loadErr) {
          reject(loadErr);
          return;
        }
        const auth = getFirebaseAuth();
        const user = auth.currentUser as FirebaseUser | null;
        const userWithDelete = user as FirebaseUser & { delete?: () => Promise<void> };
        if (!userWithDelete || typeof userWithDelete.delete !== 'function') {
          reject(new Error('Account deletion is not available for this user.'));
          return;
        }
        userWithDelete.delete().then(() => resolve()).catch(reject);
      });
    });
  }

  return {
    getCurrentUser: () => currentUser,
    isAuthReady: () => authReady,
    ensureAuth,
    getIdToken,
    signInGoogle: doSignInWithGoogle,
    signInGithub: doSignInWithGithub,
    signOut,
    subscribeUserChange(cb: () => void): () => void {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    reauthenticateCurrentUser,
    deleteCurrentUser,
    showLoginOverlay() {
      ensureAuth(() => {});
      overlay.show();
    },
    hideLoginOverlay: () => overlay.hide()
  };
}
